import styled, { css, keyframes } from 'styled-components';
import { StyledButtonProps } from './types';

const rotate = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const COLOR = {
  primary: css`
    color: ${({ theme }) => theme.color.text.neutral.inverted};
    background: ${({ theme }) => theme.color.background.brand.default};

    :hover {
      background-color: ${({ theme }) => theme.color.background.brand.medium};
    }
  `,
  secondary: css`
    color: ${({ theme }) => theme.color.text.neutral.strong};
    background: transparent;
    border: ${({ theme }) => theme.border.width.thinner}
      ${({ theme }) => theme.border.style.solid}
      ${({ theme }) => theme.color.border.neutral.strong};

    :hover {
      background-color: ${({ theme }) => theme.color.background.neutral.weak};
    }
  `,
  tertiary: css`
    color: ${({ theme }) => theme.color.background.brand.default};
    background: transparent;

    & > svg {
      fill: ${({ theme }) => theme.color.icon.brand.default};
    }
    :hover {
      background-color: ${({ theme }) => theme.color.background.brand.weakest};
    }
  `,
  danger: css`
    color: ${({ theme }) => theme.color.text.neutral.inverted};
    background: ${({ theme }) => theme.color.background.danger.default};

    :hover {
      background-color: ${({ theme }) => theme.color.background.danger.strong};
    }
  `,
  subtle: css`
    color: ${({ theme }) => theme.color.text.neutral.default};
    background: transparent;

    :hover {
      background-color: ${({ theme }) => theme.color.background.neutral.weak};
    }
  `,
  icon: css`
    background: transparent;
    padding: 0;

    :hover {
      background-color: transparent;
    }
  `,
};

export const ContainerIcon = styled.div`
  display: flex;
  margin-left: ${({ theme }) => theme.spacing.km2};
`;

function disabledBorder(
  color: string,
  borderSize: string,
  borderSolid: string,
  borderColor: string
) {
  if (color === 'tertiary' || color === 'subtle' || color === 'icon')
    return 'none';

  return `${borderSize} ${borderSolid} ${borderColor}`;
}

function disabledBackgroundBorder(color: string, backgroundColor: string) {
  if (color === 'tertiary' || color === 'subtle' || color === 'icon')
    return 'transparent';

  return backgroundColor;
}

export const Button = styled.button<StyledButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: ${({ theme }) => theme.border.radius.large};
  padding: ${({ theme }) => theme.spacing.km0}
    ${({ theme }) => theme.spacing.km4};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.m3};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  border: ${({ theme }) => theme.color.border.neutral.default};

  ${({ $width, $height, $variant, theme }) =>
    css`
      width: ${$width ? $width : 'auto'};
      height: ${$height}px;

      ${COLOR[$variant]}

      :disabled {
        color: ${theme.color.text.neutral.weak};
        border: ${disabledBorder(
          $variant,
          theme.border.width.thinner,
          theme.border.style.solid,
          theme.color.border.neutral.default
        )};
        background-color: ${disabledBackgroundBorder(
          $variant,
          theme.color.background.neutral.weak
        )};
        cursor: not-allowed;

        :hover {
          background-color: ${disabledBackgroundBorder(
            $variant,
            theme.color.background.neutral.weak
          )};
        }
      }
    `}

  & > svg[aria-label="spinner"] {
    animation: ${rotate} 1s linear infinite;
  }
`;
